/**
 * Session-expiry watch. Once Core's API auth gate enforces, a browser tab whose Cognito session has lapsed
 * keeps polling without a token and only sees 401s. This wraps window.fetch and, for a 401 on an /api call
 * that carried NO Authorization header (i.e. getAuthToken() returned null — the session is gone, not merely
 * rejected), dispatches `bridgeos:auth-required` at most once per 30 s. App.vue answers by re-authenticating.
 * A 401 on a request that did carry a token is left alone (no redirect loops on a rejected token) — unless
 * `opts.onRejectedToken(bearer)` claims it: local auth mode passes a callback that recognises (and clears) its
 * own session token, because a rejected local session must land on the login screen, never a blank page.
 */
export const AUTH_REQUIRED_EVENT = 'bridgeos:auth-required';
const MIN_INTERVAL_MS = 30_000;

function hasAuthHeader(input: RequestInfo | URL, init?: RequestInit): boolean {
  if (init?.headers) {
    const h = new Headers(init.headers as HeadersInit);
    if (h.has('Authorization') || h.has('authorization')) return true;
  }
  return typeof Request !== 'undefined' && input instanceof Request && input.headers.has('Authorization');
}
function bearerOf(input: RequestInfo | URL, init?: RequestInit): string | null {
  let v: string | null = null;
  if (init?.headers) v = new Headers(init.headers as HeadersInit).get('Authorization');
  if (!v && typeof Request !== 'undefined' && input instanceof Request) v = input.headers.get('Authorization');
  return v && v.startsWith('Bearer ') ? v.slice(7).trim() : null;
}
function isApiCall(input: RequestInfo | URL): boolean {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  return /(^|\/)api\//.test(url) || url.startsWith('/api/');
}

export interface SessionExpiryOptions {
  /**
   * Called for a 401 on an /api call that carried a bearer, with that response; resolve true to treat it as a
   * lapsed session. Such a rejection is dispatched without the 30 s debounce (the callback is expected to
   * clear the session, so it fires at most once per token). Read the body via `res.clone()`.
   */
  onRejectedToken?: (bearer: string, res: Response) => boolean | Promise<boolean>;
}

export function installSessionExpiryWatch(target: { fetch: typeof fetch; dispatchEvent: (e: Event) => boolean } = window, now: () => number = Date.now, opts: SessionExpiryOptions = {}): () => void {
  const original = target.fetch;
  let last = -Infinity;
  const wrapped: typeof fetch = async (input, init) => {
    const res = await original.call(target, input as RequestInfo, init);
    try {
      let lapsed = false;
      let rejected = false;
      if (res.status === 401 && isApiCall(input as RequestInfo | URL)) {
        if (!hasAuthHeader(input as RequestInfo | URL, init)) lapsed = true;
        else if (opts.onRejectedToken) {
          const bearer = bearerOf(input as RequestInfo | URL, init);
          rejected = !!bearer && (await opts.onRejectedToken(bearer, res)) === true;
        }
      }
      if (rejected || (lapsed && now() - last >= MIN_INTERVAL_MS)) {
        last = now();
        target.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT, { detail: { url: typeof input === 'string' ? input : String((input as URL).href ?? (input as Request).url) } }));
      }
    } catch { /* never interfere with the response */ }
    return res;
  };
  target.fetch = wrapped;
  return () => { target.fetch = original; };
}
